import { io, Socket } from "socket.io-client";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import { Store } from "@reduxjs/toolkit";
import { Persistor } from "redux-persist";
import { QueryClient } from "react-query";
import { showSessionKickedOverlay } from "../Utils/sessionKickedOverlay";
import { SESSION_INVALIDATED_MESSAGE } from "../constant/sessionConstants";
import { performStateCleanup } from "./standardizedLogout";

let socket: Socket | null = null;
let isSessionInvalidatedHandled = false;

interface ConnectSessionSocketParams {
  store: Store;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tokenSelector: (state: any) => { token: string };
  resetActions?: Array<() => { type: string }>;
  persistor?: Persistor;
  queryClient?: QueryClient;
}

/**
 * Connects to the /session WebSocket namespace.
 * Listens for "session-invalidated" events and performs logout cleanup.
 * Uses a dynamic auth callback so reconnections always use the latest token.
 */
export const connectSessionSocket = ({
  store,
  tokenSelector,
  resetActions,
  persistor,
  queryClient,
}: ConnectSessionSocketParams): void => {
  // Disconnect existing socket and reset state flags before creating a new one
  disconnectSessionSocket();
  resetSessionSocketState();

  const baseUrl = getBaseUrl(Microservice.TRADE_DIRECTORY);
  const url = `${baseUrl}/session`;

  socket = io(url, {
    path: "/trade-directory/socket.io",
    auth: (cb: (data: { token: string }) => void) => {
      const { token } = tokenSelector(store.getState());
      cb({ token });
    },
    transports: ["websocket"],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
  });

  socket.on("session-invalidated", async (data: { message?: string }) => {
    // Prevent duplicate cleanup from overlapping events
    if (isSessionInvalidatedHandled) return;
    isSessionInvalidatedHandled = true;

    // Disconnect socket before cleanup
    disconnectSessionSocket();

    // Wipe local state clean immediately (so refresh won't restore the session)
    await performStateCleanup({ store, resetActions, persistor, queryClient });

    // Show non-blocking overlay, redirect to login on dismiss
    showSessionKickedOverlay(
      data?.message || SESSION_INVALIDATED_MESSAGE,
      () => {
        isSessionInvalidatedHandled = false;
        window.location.href = "/login";
      }
    );
  });
};

/**
 * Disconnects the session socket if connected.
 */
export const disconnectSessionSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Resets the session socket state flags. Call this when user logs in
 * to ensure a fresh state after a previous session invalidation.
 */
export const resetSessionSocketState = (): void => {
  isSessionInvalidatedHandled = false;
};
